import {
  AssetMediaRef,
  assetFileShown,
  assetLink,
  QuestionnaireCollectedAssetOperation,
  QuestionnaireCollectedItem,
  QuestionnaireTurnCommand,
  QuestionnaireValidationIssue,
  TestedAssetType,
  testedAssetUnusedAssetIds,
} from "@digital-twins/shared-types";

import { ChatMessageCollect } from "../types";
import type { ChatMessageCollectAttachItem } from "@digital-twins/shared-types";

/**
 * Pure helpers for chat collect cards (composer-as-forms): what a card's
 * receipt names, where an attach card's files go, and the media a collected
 * file carries. The new assets themselves are the server's: the builder sends
 * one `collect_assets` operation (D2 S5.4).
 */

export interface CollectedAssetMediaRef extends AssetMediaRef {
  clientPreviewMediaRefs?: Array<AssetMediaRef>;
}

/** A collect's links as the builder sends them: trimmed, and a blank one skipped. */
export function collectableLinks(urls: ReadonlyArray<string>): Array<string> {
  return urls.map((url) => url.trim()).filter(Boolean);
}

/**
 * The `collect_assets` operation a collect sends. The intake stages no more
 * than the card's limit; the family's cap is Nest's write's to enforce
 * (`asset_limit_exceeded`, in the user's language).
 */
export function collectAssetsOperation(
  collect: Pick<ChatMessageCollect, "assetType">,
  items: Array<QuestionnaireCollectedItem>,
): QuestionnaireCollectedAssetOperation {
  return {
    op: "collect_assets",
    assetType: collect.assetType as TestedAssetType,
    items,
  };
}

/** A collected file's media as stored: the file, then its issued preview. */
export function persistableCollectedMedia(
  mediaRef: CollectedAssetMediaRef,
): Array<AssetMediaRef> {
  const { clientPreviewMediaRefs, ...file } = mediaRef;
  return [file, ...(clientPreviewMediaRefs ?? [])];
}

/** A file a collect card staged from a link: its media, and the link it came from. */
export interface LinkedCollectedMedia {
  mediaRef: CollectedAssetMediaRef;
  sourceUrl: string;
}

/**
 * What a collect card staged, in the order its assets are named: video
 * links, files, page captures. The link a file came from stays beside its
 * media, never on it: a media ref carries no `sourceUrl`.
 */
export interface StagedCollectMedia {
  videoLinks: ReadonlyArray<LinkedCollectedMedia>;
  files: ReadonlyArray<CollectedAssetMediaRef>;
  captures: ReadonlyArray<LinkedCollectedMedia>;
  /**
   * Page links handed over alone (`savesCaptureOfLink`): the builder's own
   * capture of each failed, so the Save captures the page from its link and
   * readiness says what it needs (H06-LP). An attach card places none.
   */
  pageLinks?: ReadonlyArray<string>;
}

/** The media an attach card places (F19), in order: each staged file's own media. */
export function stagedMediaRefsOf(
  staged: StagedCollectMedia,
): Array<CollectedAssetMediaRef> {
  return [
    ...staged.videoLinks.map(({ mediaRef }) => mediaRef),
    ...staged.files,
    ...staged.captures.map(({ mediaRef }) => mediaRef),
  ];
}

/**
 * The items a `collect_assets` operation carries, in order: a video link
 * travels as its link (it attaches as video upstream), a file as its media,
 * a page capture as its media beside the link it captured, and a page
 * link whose capture failed as its link.
 */
export function collectedItemsOf(
  staged: StagedCollectMedia,
): Array<QuestionnaireCollectedItem> {
  return [
    ...staged.videoLinks.map(({ sourceUrl }) => ({ url: sourceUrl })),
    ...staged.files.map((mediaRef) => ({
      media: persistableCollectedMedia(mediaRef),
    })),
    ...staged.captures.map(({ mediaRef, sourceUrl }) => ({
      media: persistableCollectedMedia(mediaRef),
      url: sourceUrl,
    })),
    ...(staged.pageLinks ?? []).map((url) => ({ url })),
  ];
}

type AssetRecord = Record<string, unknown>;
type TestedAssetsBlock = Record<string, unknown>;

function assetsOf(block: TestedAssetsBlock | null | undefined): Array<AssetRecord> {
  const assets = (block as { assets?: unknown } | null | undefined)?.assets;
  return Array.isArray(assets) ? (assets as Array<AssetRecord>) : [];
}

function assetId(asset: AssetRecord): string {
  return String(asset.id ?? "").trim();
}

/**
 * The assets a collect card has collected while its question is open: the
 * saved assets of the type it asks for (plan S4.1). Its receipt names them,
 * whichever write saved them: the card's own submit, a submit whose receipt
 * turn failed or was stopped, or the user's Save of assets added in the panel.
 */
export function collectedAssetKeysOf(
  savedAssets: unknown,
  assetType: string,
): Array<string> {
  return assetsOf(savedAssets as TestedAssetsBlock | null | undefined)
    .filter((asset) => asset.type === assetType)
    .map((asset) => String(asset.stableKey ?? ""))
    .filter(Boolean);
}

function assetsByKey(savedAssets: unknown): Map<string, AssetRecord> {
  return new Map(
    assetsOf(savedAssets as TestedAssetsBlock | null | undefined).map(
      (asset) => [String(asset.stableKey ?? ""), asset],
    ),
  );
}

/**
 * Whether an attach card's item has its file (F19): every asset of it (each
 * price version of a product) holds a file respondents are shown
 * (`assetFileShown`); a page link whose capture failed holds none.
 */
function attachItemHasFile(
  byKey: ReadonlyMap<string, AssetRecord>,
  item: ChatMessageCollectAttachItem,
): boolean {
  return item.assetKeys.every((key) => {
    const asset = byKey.get(key);
    return asset !== undefined && assetFileShown(asset);
  });
}

/**
 * The items an attach card still offers (F19): those of its items without
 * their file, in the card's order. A file staged on the card goes to one of
 * them; the ones that have theirs count as collected.
 */
export function openAttachItemsOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "attachTo">,
): Array<ChatMessageCollectAttachItem> {
  const byKey = assetsByKey(savedAssets);
  return (collect.attachTo ?? []).filter(
    (item) => !attachItemHasFile(byKey, item),
  );
}

/**
 * What a collect card's receipt names while its question is open. A card
 * whose files are the items: the saved assets of its type. An attach card
 * (F19): the assets of its items that have their file, which is none when the
 * user continued without one.
 */
export function receiptAssetKeysOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "assetType" | "attachTo">,
): Array<string> {
  if (!collect.attachTo) {
    return collectedAssetKeysOf(savedAssets, collect.assetType);
  }
  const byKey = assetsByKey(savedAssets);
  return collect.attachTo
    .filter((item) => attachItemHasFile(byKey, item))
    .flatMap((item) => item.assetKeys);
}

/** How many of an attach card's items have their file (F19). */
export function attachItemsWithFileOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "attachTo">,
): number {
  const byKey = assetsByKey(savedAssets);
  return (collect.attachTo ?? []).filter((item) =>
    attachItemHasFile(byKey, item),
  ).length;
}

/** Every item on an attach collect card already has media on the saved draft. */
export function attachCollectSatisfied(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "attachTo">,
): boolean {
  if (!collect.attachTo?.length) {
    return false;
  }
  return openAttachItemsOf(savedAssets, collect).length === 0;
}

/**
 * Which item each file goes on (F19): the item the user put it on, else the
 * next item without a file, in order. Only an item still without its file in
 * the saved block takes one, so a file added in between (the panel, another
 * tab) is never replaced; a file for any other item is not placed.
 */
export function attachPlacementsOf(
  savedAssets: unknown,
  collect: Pick<ChatMessageCollect, "attachTo">,
  mediaRefs: ReadonlyArray<CollectedAssetMediaRef>,
  targets?: ReadonlyArray<ChatMessageCollectAttachItem>,
): Array<{ item: ChatMessageCollectAttachItem; mediaRef: CollectedAssetMediaRef }> {
  const open = openAttachItemsOf(savedAssets, collect);
  const isOpen = (item: ChatMessageCollectAttachItem) =>
    open.some((candidate) => candidate.assetKeys.join("\n") === item.assetKeys.join("\n"));
  return mediaRefs.flatMap((mediaRef, index) => {
    const item = targets?.[index] ?? open[index];
    return item && isOpen(item) ? [{ item, mediaRef }] : [];
  });
}

/**
 * An attach card's files on its items (F19): each file becomes the media of
 * every asset of the item it was placed on (each price version of a product
 * alike). Nothing is added, renamed or re-described, so the write is only
 * those assets' media.
 */
export function attachCollectedMedia(
  block: TestedAssetsBlock,
  placements: ReadonlyArray<{
    item: ChatMessageCollectAttachItem;
    mediaRef: CollectedAssetMediaRef;
  }>,
): TestedAssetsBlock {
  // The files placed on an item are its media, in order: the card places no
  // more on one item than it holds (`FILES_PER_ASSET`).
  const assets = assetsOf(block).map((asset) => {
    const placed = placements.filter(({ item }) =>
      item.assetKeys.includes(String(asset.stableKey ?? "")),
    );
    return placed.length === 0
      ? { ...asset }
      : {
          ...asset,
          media: placed.flatMap(({ mediaRef }) =>
            persistableCollectedMedia(mediaRef),
          ),
        };
  });
  return { ...block, assets };
}

type CollectReceipt = Extract<
  QuestionnaireTurnCommand,
  { kind: "collect_receipt" }
>;

/** A primary file of a media list: never one of its slices. */
function primaryMediaOf(media: ReadonlyArray<AssetMediaRef>): Array<AssetMediaRef> {
  return media.filter((ref) => !ref.sliceOf);
}

/**
 * Which item got which file, for an attach card's receipt (F19): each of the
 * card's items whose first asset the attach write put media on, with the
 * name of the file it sent (the upload's own name), or null. Read from that
 * write's own `set_asset_media` operations (a receipt's `assetOperations`),
 * never the draft's media now: a file replaced or an asset removed later
 * leaves the message as sent (E-session-02).
 */
export function attachReceiptLinesOf(
  collect: Pick<ChatMessageCollect, "attachTo">,
  operations: ReadonlyArray<QuestionnaireCollectedAssetOperation>,
): Array<{ item: string; file: string | null }> {
  const sent = new Map(
    operations.flatMap((operation) =>
      operation.op === "set_asset_media"
        ? [[operation.target, primaryMediaOf(operation.media)] as const]
        : [],
    ),
  );
  return (collect.attachTo ?? []).flatMap((item) => {
    const media = sent.get(item.assetKeys[0]);
    if (!media || media.length === 0) return [];
    const file = media[0].label ?? media[0].altText;
    return [
      {
        item: item.label,
        file: typeof file === "string" && file.trim() ? file.trim() : null,
      },
    ];
  });
}

/** A file a receipt sent, as the chat's sent-attachments bubble shows it. */
export interface SentReceiptMedia {
  src: string;
  mimeType?: string;
  label?: string;
}

/**
 * The files a collect receipt sent, from its own operations: each collected
 * item's, and each asset's an attach put media on (once per file: a
 * product's price versions share theirs). A file shows by its first image or
 * video, which for a PDF is its rendered first page. The draft's media now
 * are never read, so a later replace, removal or reused key leaves the
 * user's message as they sent it (E-session-02).
 */
export function receiptSentMedia(receipt: CollectReceipt): Array<SentReceiptMedia> {
  const files = (receipt.assetOperations ?? []).flatMap((operation) => {
    if (operation.op === "collect_assets") {
      return operation.items.flatMap((item) =>
        "media" in item && item.media ? [item.media] : [],
      );
    }
    if (operation.op === "set_asset_media") return [operation.media];
    return [];
  });
  const seen = new Set<string>();
  return files.flatMap((media) => {
    const shown = media.find(
      (ref) => (ref.kind === "image" || ref.kind === "video") && !ref.sliceOf,
    );
    const src = shown?.publicUrl || shown?.url || shown?.filePath || "";
    if (!shown || !src || seen.has(src)) return [];
    seen.add(src);
    // A persisted ref that lost its mime still says it is a video by its kind.
    const mimeType =
      shown.mimeType ?? (shown.kind === "video" ? "video/mp4" : undefined);
    const label = primaryMediaOf(media)[0]?.label ?? shown.label;
    return [{ src, mimeType, ...(label ? { label } : {}) }];
  });
}

/** The stable keys of every asset a tested-asset block holds. */
export function assetKeysOf(savedAssets: unknown): Array<string> {
  return assetsOf(savedAssets as TestedAssetsBlock | null | undefined)
    .map((asset) => String(asset.stableKey ?? ""))
    .filter(Boolean);
}

/**
 * Of the assets a write just added (`addedKeys`), the ones no question shows
 * yet (04 I-19): what the "update the questions?" card offers to bring in.
 * The write's readiness is the verdict: it names such an asset
 * `tested_asset_unused`, and never an asset a per-version binding, a
 * tested-asset grid or a typed roster already takes in, nor any asset of a
 * draft with no questions.
 */
export function uncoveredAssetsAmong(
  addedKeys: ReadonlyArray<string>,
  accepted: {
    draft: unknown;
    readiness?: { blockingIssues: Array<QuestionnaireValidationIssue> } | null;
  },
): Array<{ stableKey: string; type: string }> {
  const unused = testedAssetUnusedAssetIds(
    accepted.readiness?.blockingIssues ?? [],
  );
  const added = new Set(addedKeys);
  return assetsOf(
    (accepted.draft as { testedAssets?: TestedAssetsBlock } | null)
      ?.testedAssets,
  )
    .filter(
      (asset) =>
        added.has(String(asset.stableKey ?? "")) && unused.has(assetId(asset)),
    )
    .map((asset) => ({
      stableKey: String(asset.stableKey),
      type: String(asset.type ?? ""),
    }));
}

/**
 * A `collect_assets` write's replay rule: the draft already holds every item
 * it collected (a file by its media id, a link by its address), so a lost
 * first response was this write's.
 */
export function carriesCollectedItems(
  items: ReadonlyArray<QuestionnaireCollectedItem>,
): (latest: { testedAssets?: unknown }) => boolean {
  return (latest) => {
    const assets = assetsOf(latest.testedAssets as TestedAssetsBlock | null);
    const mediaIds = new Set(
      assets.flatMap((asset) =>
        Array.isArray(asset.media)
          ? (asset.media as Array<{ id?: unknown }>).map((ref) => String(ref.id))
          : [],
      ),
    );
    const links = new Set(
      assets.map((asset) => (assetLink(asset) ?? "").toLowerCase()),
    );
    return items.every((item) =>
      "media" in item
        ? mediaIds.has(item.media[0].id)
        : links.has(item.url.trim().toLowerCase()),
    );
  };
}
